import { useGlobalState } from '@vindr/workspace-core/contexts/GlobalContext';
import { useChangeStatusOrders } from '@vindr/services/actions/order.action';
import { OrderNote } from '@vindr/services/apis/order.api';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import {
  actionStatusMapping,
  ORDER_PRIORITY,
  ORDER_STATUS,
  statusLabelMap,
  statusMappingAction,
} from '@vindr/services/constants/order.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import keycloak from '@vindr/services/keycloak';
import { Empty, FormItem } from '@vindr/ui';
import dayjs from '@vindr/utils/day';
import { getDateFormatted } from '@vindr/utils/helpers/common.helper';
import { Button, Col, Form, Input, Row, Select, Space } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual';

export interface ChangeStatusFormProps {
  defaultValues?: {
    order_id: string | string[];
    priority: string;
    notes: OrderNote[];
    initStatus: ORDER_STATUS;
    requestStatus: ORDER_STATUS;
    isOpenFromContextMenu: boolean;
    study_instance_uids?: string[];
  };
  onFinish?: () => void;
  onCancel: () => void;
}

const ChangeStatusForm = React.memo(
  ({ defaultValues, onFinish, onCancel }: ChangeStatusFormProps) => {
    const { t } = useTranslation('Vindoc');
    const { message } = useGlobalState();
    const { mutate: changeStatusOrder } = useChangeStatusOrders();

    const [labelDateAndUser, setLabelDateAndUser] = useState({
      label: null,
      user: null,
    });
    const [changeStatusForm] = Form.useForm();
    const currentStatus: ORDER_STATUS = Form.useWatch('status', changeStatusForm);

    const statusAvailable = useMemo(() => {
      const isMatched = defaultValues?.study_instance_uids.length > 0; // can not cancal a matched order
      if (!defaultValues?.isOpenFromContextMenu) {
        return (statusMappingAction[ORDER_STATUS[defaultValues?.initStatus]] || [])
          .map((it) => actionStatusMapping[it])
          .filter((status: ORDER_STATUS) => {
            return !(isMatched && status === ORDER_STATUS.CANCEL);
          });
      }
      return Object.keys(ORDER_STATUS).filter((it) => !isNaN(Number(it)));
    }, [
      defaultValues?.initStatus,
      defaultValues?.isOpenFromContextMenu,
      defaultValues?.study_instance_uids.length,
    ]);

    const handleSave = async () => {
      if (!keycloak.hasOneOfPerm([PERMISSIONS.order.changestatus])) {
        message.error(t(MESSAGE.ERROR.EMSG_0002));
        return;
      }

      try {
        const values = await changeStatusForm.validateFields();

        changeStatusOrder(
          {
            order_ids: Array.isArray(defaultValues?.order_id)
              ? defaultValues?.order_id
              : [defaultValues?.order_id],
            status: values.status,
            priority: ORDER_PRIORITY[values.priority as keyof typeof ORDER_PRIORITY] || null,
            notes: values.new_note ? [values.new_note as string] : [],
            order_status_audit: {
              audit_time: dayjs(values.action_at).toISOString(),
              update_by_name: values.action_by,
              update_by_username: keycloak.getUserInfo().username,
            },
          },
          {
            onSuccess: () => {
              message.success(t(MESSAGE.SUCCESS.SCD_8001));
              onFinish();
            },
            onError: (error: any) => {
              const error_message =
                (error?.response?.data?.error_code &&
                  MESSAGE.ERROR[error?.response?.data?.error_code]) ||
                'Update error';

              message.error(t(error_message));
            },
          }
        );
      } catch (error: any) {
        message.error(t(MESSAGE.ERROR.EMSG_0001));
      }
    };

    const handleOnChangeStatus = (status: number) => {
      const labelAndUser = statusLabelMap[ORDER_STATUS[status]];
      if (labelAndUser) setLabelDateAndUser(labelAndUser);
    };

    useEffect(() => {
      if (defaultValues?.notes) {
        const notesText = defaultValues?.notes
          .slice()
          .sort((a, b) => dayjs(b.time).valueOf() - dayjs(a.time).valueOf())
          .map((note) => {
            const name = note?.user || '';
            const formattedDate = dayjs(note?.time).format(getDateFormatted(true)) || '';
            return `${formattedDate} - (${name}): ${note?.text}`;
          })
          .join('\n\n');
        changeStatusForm.setFieldsValue({ note: notesText });
      }

      changeStatusForm.setFieldsValue({
        action_by: keycloak.getUserInfo().username || keycloak.getUserInfo().fullname || '',
      });
      const labelAndUser =
        statusLabelMap[
          defaultValues?.isOpenFromContextMenu
            ? ORDER_STATUS[defaultValues?.requestStatus]
            : ORDER_STATUS[statusAvailable[0]]
        ];
      if (labelAndUser) setLabelDateAndUser(labelAndUser);
    }, [defaultValues, changeStatusForm, statusAvailable]);

    const initialStatus = useMemo(() => {
      return defaultValues?.isOpenFromContextMenu
        ? defaultValues?.requestStatus
        : statusAvailable[0];
    }, [defaultValues?.isOpenFromContextMenu, defaultValues?.requestStatus, statusAvailable]);

    const initialPriority = useMemo(() => {
      return defaultValues?.isOpenFromContextMenu ||
        (defaultValues?.priority && defaultValues?.order_id.length === 1)
        ? defaultValues?.priority
        : null;
    }, [defaultValues?.isOpenFromContextMenu, defaultValues?.priority, defaultValues?.order_id]);

    return (
      <Form
        form={changeStatusForm}
        layout="vertical"
        initialValues={{
          status: initialStatus,
          priority: initialPriority,
          action_at: dayjs().toDate(),
        }}
        onFinish={onFinish}
        onFinishFailed={() => message.error(t(MESSAGE.ERROR.EMSG_0001))}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={t('Status')}
              name="status"
              rules={[{ required: true, message: t('Please select status') }]}
            >
              <Select
                placeholder={t('Select status')}
                onChange={handleOnChangeStatus}
                disabled={defaultValues?.isOpenFromContextMenu}
              >
                {statusAvailable.map((status: ORDER_STATUS) => (
                  <Select.Option key={status} value={Number(status)}>
                    {t(ORDER_STATUS[status])}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Form.Item
              label={t('Priority')}
              name="priority"
              rules={[
                {
                  required: Array.isArray(defaultValues?.order_id) ? false : true,
                  message: t('Please select priority'),
                },
              ]}
            >
              <Select
                placeholder={t('Select priority')}
                disabled={!keycloak.hasOneOfPerm([PERMISSIONS.order.priority])}
              >
                {Object.keys(ORDER_PRIORITY)
                  .filter((key) => isNaN(Number(key)))
                  .map((priority) => (
                    <Select.Option key={priority} value={priority}>
                      {t(priority)}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={12}>
            <FormItem.DatePicker
              label={t(labelDateAndUser?.label)}
              name="action_at"
              showTime
              rules={[{ required: true, message: t('Please select date') }]}
              disabled={currentStatus !== ORDER_STATUS.SCHEDULED}
            />
          </Col>
          <Col xs={24} sm={24} md={12}>
            <Form.Item label={t(labelDateAndUser?.user)} name="action_by">
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>
        {defaultValues.isOpenFromContextMenu ? (
          <Row gutter={[16, 16]}>
            <Col sm={24}>
              <Form.Item label={t('Note')} name="note">
                {defaultValues?.notes?.length ? (
                  <Input.TextArea
                    rows={defaultValues?.notes?.length * 2}
                    readOnly
                    style={{ resize: 'none', maxHeight: 200, overflowY: 'auto' }}
                  />
                ) : (
                  <Empty description={t('No notes')} />
                )}
              </Form.Item>
            </Col>
            {keycloak.hasOneOfPerm([PERMISSIONS.order.note]) && (
              <Col sm={24}>
                <Form.Item label={t('New Note')} name="new_note">
                  <Input.TextArea rows={4} style={{ resize: 'none' }} />
                </Form.Item>
              </Col>
            )}
          </Row>
        ) : null}
        <Row justify="end" gutter={[16, 16]}>
          <Col>
            <Space>
              <Button
                onClick={() => {
                  onCancel();
                  changeStatusForm.resetFields();
                }}
              >
                {t('Cancel')}
              </Button>
              <Button type="primary" htmlType="submit" onClick={() => handleSave()}>
                {t('Save')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    );
  },
  (prev, next) => {
    return isEqual(prev.defaultValues, next.defaultValues);
  }
);

export default ChangeStatusForm;

ChangeStatusForm.displayName = 'ChangeStatusForm';
